import { formatTime } from '@platform/design-system';
import { ArrowRight, ExternalLink } from 'lucide-react';
import type { FieldChange, HistoryEntry } from './history.ts';
import {
  actorNameOf,
  fieldLabelOf,
  formatFieldValue,
  isCreation,
  isLtrField,
  sentenceOf,
  type ValueContext,
} from './history-format.ts';
import styles from './History.module.css';

export type HistoryLineProps = {
  entry: HistoryEntry;
  context: ValueContext;
  onFilterActor: (userId: string) => void;
  onOpenSubject: ((entry: HistoryEntry) => void) | null;
};

const Value = ({ field, text }: { field: string; text: string }) =>
  isLtrField(field) ? <bdi dir="ltr">{text}</bdi> : <>{text}</>;

type ChangeListProps = {
  changes: readonly FieldChange[];
  context: ValueContext;
  valuesOnly: boolean;
};

const ChangeList = ({ changes, context, valuesOnly }: ChangeListProps) => (
  <dl className={styles['changes']}>
    {changes.map((change) => (
      <div key={change.field} className={styles['change']}>
        <dt>{fieldLabelOf(change.field, context.t)}</dt>
        <dd>
          {valuesOnly ? null : (
            <>
              <span className={styles['before']}>
                <span className="visually-hidden">{context.t('history.before')} </span>
                <Value field={change.field} text={formatFieldValue(change.field, change.before, context)} />
              </span>{' '}
              <ArrowRight size={16} aria-hidden="true" className="flip-inline" />{' '}
            </>
          )}
          <span className={styles['after']}>
            {valuesOnly ? null : <span className="visually-hidden">{context.t('history.after')} </span>}
            <Value field={change.field} text={formatFieldValue(change.field, change.after, context)} />
          </span>
        </dd>
      </div>
    ))}
  </dl>
);

export const HistoryLine = ({ entry, context, onFilterActor, onOpenSubject }: HistoryLineProps) => {
  const { t, locale } = context;
  const actorName = actorNameOf(entry.actor, t);
  const actorId = entry.actor.kind === 'person' ? entry.actor.userId : null;
  return (
    <li className={styles['line']}>
      <time className={styles['time']} dateTime={entry.occurredAt}>
        {formatTime(entry.occurredAt, locale)}
      </time>
      <div className={styles['body']}>
        <p className={styles['sentence']}>
          {actorId === null ? (
            <strong>{actorName}</strong>
          ) : (
            <button
              type="button"
              className={styles['link']}
              title={t('history.onlyActor', { name: actorName })}
              onClick={() => {
                onFilterActor(actorId);
              }}
            >
              {actorName}
            </button>
          )}{' '}
          <span>{sentenceOf(entry, t)}</span>
        </p>
        {entry.changes.length === 0 ? null : (
          <ChangeList changes={entry.changes} context={context} valuesOnly={isCreation(entry.action)} />
        )}
      </div>
      {onOpenSubject === null ? null : (
        <button
          type="button"
          className={styles['open']}
          aria-label={t('history.openSubject', { subject: entry.label ?? sentenceOf(entry, t) })}
          onClick={() => {
            onOpenSubject(entry);
          }}
        >
          <ExternalLink size={16} aria-hidden="true" />
          <span className={styles['openLabel']}>{t('history.open')}</span>
        </button>
      )}
    </li>
  );
};
